import { type ZodType } from 'zod'
import {
  apiErrorBodySchema,
  type ApiErrorBody,
} from '@lib/dto/api-error.dto'
import { tokenStorage } from './tokenStorage'
import logger, { toErrorMessage } from './logger'

/**
 * The fetch-backed HTTP client for the web app. Every response is validated
 * with a Zod schema at the network boundary — the client never launders a raw
 * `.json()` payload into a concrete type (`swocrm/no-untyped-fetch-json`). The
 * schema is both the validator and the source of the returned type.
 *
 * Usage:
 *   const invite = await api.get('/invites/1', inviteDtoSchema) // InviteDto
 *   const maybe = await api.getNullable('/invites/1', inviteDtoSchema) // InviteDto | null
 *
 * `get<T>` asserts the endpoint always returns a `T`. For endpoints that can
 * legitimately return no content (empty body / 204 / a bare `null`), use
 * `getNullable<T>`, which resolves an absent body to `null` and types the
 * result `T | null` (`swocrm/prefer-get-nullable`).
 */

/** The base URL of the API. Overridable per environment via `VITE_API_URL`. */
const API_BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8000'

/**
 * An absolute URL on the API, for the rare address the browser loads itself
 * rather than through `api` — an `<img src>` pointing at a redirect route.
 * Anything that returns data goes through `api`, which validates it.
 */
export function apiUrl(path: string): string {
  return `${API_BASE_URL}${path}`
}

export type { ApiErrorBody }

/**
 * Thrown for any non-2xx response (and for network failures, with `status: 0`).
 * Carries the HTTP status and the parsed error body so callers and React Query
 * error states can branch on `status` or surface `body.message`.
 */
export class ApiError extends Error {
  readonly status: number
  readonly body: ApiErrorBody

  constructor(status: number, body: ApiErrorBody) {
    super(body.message ?? body.error ?? `Request failed with status ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

/** Type guard for {@link ApiError} that narrows without an `unknown` annotation. */
export function isApiError<E>(error: E): error is E & ApiError {
  return error instanceof ApiError
}

/** Options accepted by every request method. */
export interface RequestOptions {
  /** Extra headers merged over the defaults. */
  headers?: Record<string, string>
  /** AbortController signal for cancellation. */
  signal?: AbortSignal
  /**
   * Let the request outlive the page. For a small batch sent as a tab is
   * hidden or closed (the shared proposal's activity), which would otherwise
   * be cancelled with the page.
   */
  keepalive?: boolean
}

/** A JSON-serializable request body — the call site supplies its concrete shape. */
export type RequestBody = object

/**
 * Parse a text body as JSON, treating an empty body as `null`. Returns `any`
 * (JSON.parse) by design: the value is immediately handed to `schema.parse`,
 * which validates it and produces the typed result.
 */
function parseJson(text: string) {
  return text.trim() === '' ? null : JSON.parse(text)
}

/**
 * Path segments that are credentials rather than identifiers, and so must not
 * reach a log. An invite token is a bearer credential for creating an account:
 * anyone holding it can claim the invite, so it is redacted the same way a
 * password would be. A shared proposal's token is the same kind of thing: it
 * opens a client's proposal with no login. Keyed by the path prefix that
 * precedes the secret segment.
 */
const SECRET_PATH_PREFIXES = ['/invites/accept/', '/shared-proposals/'] as const

/**
 * Redact credential-bearing segments of a request path so logs stay useful
 * (you can still see which endpoint failed) without recording the secret.
 */
export function redactPath(path: string): string {
  for (const prefix of SECRET_PATH_PREFIXES) {
    if (path.startsWith(prefix)) {
      return `${prefix}<redacted>`
    }
  }
  return path
}

/** Extract a typed error body from a raw response text (never throws). */
function readErrorBody(text: string): ApiErrorBody {
  if (text.trim() === '') return {}
  try {
    const result = apiErrorBodySchema.safeParse(JSON.parse(text))
    return result.success ? result.data : { message: text }
  } catch {
    return { message: text }
  }
}

/**
 * Perform the HTTP request, applying the base URL, auth header, and JSON body
 * encoding, and translating a failed response into a thrown {@link ApiError}.
 * Returns the raw response text so each method can parse it with its schema.
 */
async function send(
  method: string,
  path: string,
  body: RequestBody | undefined,
  options: RequestOptions | undefined,
): Promise<string> {
  const headers: Record<string, string> = { ...options?.headers }
  if (body !== undefined && headers['Content-Type'] === undefined) {
    headers['Content-Type'] = 'application/json'
  }

  const token = tokenStorage.get()
  if (token) headers['Authorization'] = `Bearer ${token}`

  let response: Response
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(options?.signal ? { signal: options.signal } : {}),
      ...(options?.keepalive === true ? { keepalive: true } : {}),
    })
  } catch (error) {
    // Rethrow caller-initiated cancellation so callers can detect it.
    if (error instanceof Error && error.name === 'AbortError') throw error
    const message = toErrorMessage(error)
    logger.error('Network request failed', {
      path: redactPath(path),
      error: message,
    })
    throw new ApiError(0, { message })
  }

  const text = await response.text()

  if (!response.ok) {
    // A 401 means the stored token is no longer valid — drop it so the app
    // falls back to the signed-out state on the next auth check.
    if (response.status === 401) tokenStorage.clear()
    const errorBody = readErrorBody(text)
    logger.error('API request failed', {
      path: redactPath(path),
      status: response.status,
      message: errorBody.message ?? errorBody.error ?? null,
    })
    throw new ApiError(response.status, errorBody)
  }

  return text
}

export const api = {
  /** GET a resource that is always present; validates the body with `schema`. */
  async get<T>(
    path: string,
    schema: ZodType<T>,
    options?: RequestOptions,
  ): Promise<T> {
    const text = await send('GET', path, undefined, options)
    return schema.parse(parseJson(text))
  },

  /**
   * GET a resource that may be absent. An empty body / 204 / bare `null`
   * resolves to `null`; otherwise the body is validated with `schema`.
   */
  async getNullable<T>(
    path: string,
    schema: ZodType<T>,
    options?: RequestOptions,
  ): Promise<T | null> {
    const text = await send('GET', path, undefined, options)
    const parsed = parseJson(text)
    if (parsed === null) return null
    return schema.parse(parsed)
  },

  /** POST a JSON body and validate the response with `schema`. */
  async post<T>(
    path: string,
    body: RequestBody,
    schema: ZodType<T>,
    options?: RequestOptions,
  ): Promise<T> {
    const text = await send('POST', path, body, options)
    return schema.parse(parseJson(text))
  },

  /**
   * POST a JSON body to an endpoint that answers with no content (204).
   *
   * No schema, because there is nothing to validate; a body that does come
   * back is ignored rather than trusted.
   */
  async postNoContent(
    path: string,
    body: RequestBody,
    options?: RequestOptions,
  ): Promise<void> {
    await send('POST', path, body, options)
  },

  /** PUT a JSON body and validate the response with `schema`. */
  async put<T>(
    path: string,
    body: RequestBody,
    schema: ZodType<T>,
    options?: RequestOptions,
  ): Promise<T> {
    const text = await send('PUT', path, body, options)
    return schema.parse(parseJson(text))
  },

  /** DELETE a resource. Resolves once the server confirms; no body is returned. */
  async del(path: string, options?: RequestOptions): Promise<void> {
    await send('DELETE', path, undefined, options)
  },
}
